import { useState } from "react";
import { useSnackbar } from "notistack";
import {
  MenuItem,
  Select,
  CircularProgress,
  Typography,
  FormControl,
  Box,
  Button,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { useRuns } from "contexts/RunsContext";
import { useAuth } from "contexts/AuthContext";
import usePage from "hooks/usePage";
import { API_URL, HASH_API_TOKEN } from "config";
import { managedFetch } from "utils/FetchManager";
import { fetchIntermixGeoJSON, withArrowAccept } from "utils/intermixArrow";
import { Loader } from "components/UiComponents";
import PlaybackControl from "components/map/PlaybackControl";
import { downloadAsFormat, filterFeaturesByNodeType } from "utils/exportData";
import {
  EXPORT_FORMATS,
  NODE_TYPE_SUBSETS,
  URBAN_DOWNLOAD_TYPES,
} from "constants/exportData";
import { countNodeTypes } from "utils/urbanNodes";

function formatVS(v, s) {
  const vStr = v != null ? v : "—";
  const sStr = s != null ? s : "—";
  return `V: ${vStr}, S: ${sStr}`;
}

function formatRunLabel(run, iterations, loadedV, loadedS) {
  const apiV = run?.ignited_vegetation_count;
  const apiS = run?.ignited_structure_count;
  if (apiV != null || apiS != null) {
    return `${run.run_index} / ${iterations} - ${formatVS(apiV, apiS)}`;
  }
  if (loadedV != null || loadedS != null) {
    return `${run.run_index} / ${iterations} - ${formatVS(loadedV, loadedS)}`;
  }
  return `${run.run_index} / ${iterations}`;
}

export default function UrbanMapControl() {
  const {
    intermixSymbolKey,
    setIntermixSymbolKey,
    intermixResults,
    intermixRunID,
    intermixScenarioSequence,
    intermixScenarioTree,
    isLoadingIntermixSummary,
    isLoadingIntermixExtended,
    selectedIntermixRunIndex,
    setSelectedIntermixRunIndex,
    intermixRunMetadata,
    intermixRunsList,
  } = useRuns();

  const { enqueueSnackbar } = useSnackbar();
  const { runId: pageRunId, isSharePage } = usePage();
  const { currentUser } = useAuth();
  const [downloadType, setDownloadType] = useState("aggregate");
  const [downloadNodeType, setDownloadNodeType] = useState("all");
  const [downloadFormat, setDownloadFormat] = useState("geojson");
  const [isExporting, setIsExporting] = useState(false);

  const iterations = intermixRunMetadata?.iterations ?? 1;
  const durationMinutes = intermixRunMetadata?.durationMinutes ?? 240;

  // Fallback V/S counts from loaded sequence when API counts are null
  const { structure: loadedSCount, vegetation: loadedVCount } = countNodeTypes(
    intermixScenarioSequence
  );
  const hasLoadedCounts = intermixScenarioSequence?.features?.length > 0;

  const selectedRunData = intermixRunsList?.find(
    (r) => r.run_index === selectedIntermixRunIndex
  );
  const runSelectorLabel = selectedRunData
    ? formatRunLabel(
        selectedRunData,
        iterations,
        hasLoadedCounts ? loadedVCount : null,
        hasLoadedCounts ? loadedSCount : null
      )
    : `${selectedIntermixRunIndex} / ${iterations}`;

  const isPolygonDownload =
    downloadType === "aggregate_polygons" || downloadType === "polygons";

  // Building footprints aren't loaded for map display, so fetch them on demand
  // from the intermix ?geometry=polygon endpoints (structures only).
  const fetchPolygons = async () => {
    const prefix = isSharePage ? "/hash" : "";
    const bearerToken = isSharePage ? HASH_API_TOKEN : currentUser?.accessToken;
    const authHeaders = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    };
    const url =
      downloadType === "aggregate_polygons"
        ? `${API_URL}${prefix}/simulation/intermix/${pageRunId}?geometry=polygon`
        : `${API_URL}${prefix}/simulation/intermix/${pageRunId}/${selectedIntermixRunIndex}/nodes?geometry=polygon`;

    const { response, data } = await fetchIntermixGeoJSON(
      managedFetch(
        url,
        withArrowAccept({ headers: authHeaders }),
        isSharePage ? null : currentUser
      )
    );
    if (!response?.ok || data?.type !== "FeatureCollection") {
      return null;
    }
    return data;
  };

  const handleDownloadData = async () => {
    const runId = intermixRunID || "run";
    const applyNodeFilter =
      (downloadType === "aggregate" || downloadType === "nodes") &&
      downloadNodeType !== "all";

    setIsExporting(true);
    try {
      let data;
      let filenameBase;

      if (isPolygonDownload) {
        data = await fetchPolygons();
        if (!data) {
          enqueueSnackbar("Failed to load building polygons.", {
            variant: "error",
          });
          return;
        }
        filenameBase =
          downloadType === "aggregate_polygons"
            ? `intermix_aggregate_polygons-${runId}`
            : `intermix_polygons-${runId}@${selectedIntermixRunIndex}`;
      } else if (downloadType === "aggregate") {
        data = applyNodeFilter
          ? filterFeaturesByNodeType(intermixResults, downloadNodeType)
          : intermixResults;
        filenameBase = `intermix_aggregate-${runId}`;
      } else if (downloadType === "tree") {
        data = intermixScenarioTree;
        filenameBase = `intermix_tree-${runId}@${selectedIntermixRunIndex}`;
      } else {
        data = applyNodeFilter
          ? filterFeaturesByNodeType(intermixScenarioSequence, downloadNodeType)
          : intermixScenarioSequence;
        filenameBase = `intermix_node-${runId}@${selectedIntermixRunIndex}`;
      }

      if (!data) return;
      if (!data.features?.length) {
        enqueueSnackbar(
          isPolygonDownload
            ? "No building polygons available for this run."
            : "No features match the selected filter.",
          { variant: "warning" }
        );
        return;
      }

      await downloadAsFormat(data, filenameBase, downloadFormat);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const isNodeTypeFilterVisible =
    downloadType === "aggregate" || downloadType === "nodes";
  const hasData =
    isPolygonDownload ||
    (downloadType === "aggregate" && intermixResults?.features?.length) ||
    (downloadType === "tree" && intermixScenarioTree?.features?.length) ||
    (downloadType === "nodes" && intermixScenarioSequence?.features?.length);

  return (
    <>
      {isLoadingIntermixSummary ? (
        <Loader size={50} />
      ) : (
        <>
          {intermixResults && (
            <FormControl fullWidth variant="standard" sx={{ mb: 1 }}>
              <Select
                value={selectedIntermixRunIndex}
                onChange={(e) =>
                  setSelectedIntermixRunIndex(Number(e.target.value))
                }
                displayEmpty
                renderValue={() => runSelectorLabel}
                inputProps={{ "aria-label": "Select run" }}
              >
                {(
                  intermixRunsList ??
                  Array.from({ length: iterations }, (_, i) => ({
                    run_index: i + 1,
                  }))
                ).map((run) => (
                  <MenuItem key={run.run_index} value={run.run_index}>
                    {formatRunLabel(
                      run,
                      iterations,
                      run.run_index === selectedIntermixRunIndex &&
                        hasLoadedCounts
                        ? loadedVCount
                        : null,
                      run.run_index === selectedIntermixRunIndex &&
                        hasLoadedCounts
                        ? loadedSCount
                        : null
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Select
            variant="standard"
            labelId="select-style-label"
            id="select-style"
            value={intermixSymbolKey}
            onChange={(event) => setIntermixSymbolKey(event.target.value)}
            fullWidth
            sx={{ my: 1 }}
          >
            <MenuItem disabled>--- Aggregate Simulation ---</MenuItem>
            <MenuItem value="ignited">Aggregate Ignition Probability</MenuItem>
            <MenuItem value="ignition_time">
              Aggregate Mean Ignition Time
            </MenuItem>
            <MenuItem value="node_type">Aggregate Node Type</MenuItem>
            <MenuItem value="node_importance">
              Aggregate Node Importance
            </MenuItem>
            <MenuItem value="ignition_reason">
              Aggregate Ignition Reason
            </MenuItem>
            <MenuItem disabled>--- Experimental Layers ---</MenuItem>
            {isLoadingIntermixExtended ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Loading...
              </MenuItem>
            ) : (
              [
                <MenuItem key="run_1_tree" value="run_1_tree">
                  Run {selectedIntermixRunIndex}: Ignition Tree
                </MenuItem>,
                <MenuItem key="run_1_sequence" value="run_1_sequence">
                  Run {selectedIntermixRunIndex}: Ignition Sequence
                </MenuItem>,
              ]
            )}
          </Select>

          {intermixResults && intermixSymbolKey === "run_1_tree" && (
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Playback: {durationMinutes} min
              </Typography>
              <PlaybackControl
                maxAnimationTime={durationMinutes}
                animateKey={"time"}
                animateLayer="intermix-tree-filter"
              />
            </Box>
          )}

          {intermixResults && intermixSymbolKey === "run_1_sequence" && (
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Playback: {durationMinutes} min
              </Typography>
              <PlaybackControl
                maxAnimationTime={durationMinutes}
                animateKey={"ignition_time"}
                animateLayer="intermix-sequence"
              />
            </Box>
          )}

          {intermixResults && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Download Data
              </Typography>
              <FormControl fullWidth variant="standard" sx={{ mb: 1.5 }}>
                <Select
                  value={downloadType}
                  onChange={(e) => setDownloadType(e.target.value)}
                  displayEmpty
                >
                  {URBAN_DOWNLOAD_TYPES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isNodeTypeFilterVisible ? (
                <FormControl fullWidth variant="standard" sx={{ mb: 1.5 }}>
                  <Select
                    value={downloadNodeType}
                    onChange={(e) => setDownloadNodeType(e.target.value)}
                    displayEmpty
                  >
                    {NODE_TYPE_SUBSETS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              <FormControl fullWidth variant="standard" sx={{ mb: 1.5 }}>
                <Select
                  value={downloadFormat}
                  onChange={(e) => setDownloadFormat(e.target.value)}
                  displayEmpty
                >
                  {EXPORT_FORMATS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={handleDownloadData}
                disabled={!hasData || isExporting}
                fullWidth
                sx={{ mt: 1 }}
              >
                {isExporting ? "Exporting..." : "Download"}
              </Button>
            </Box>
          )}
        </>
      )}
    </>
  );
}
